import React from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { ExternalLink, Instagram } from 'lucide-react';
import { mockData } from '../mock';

const Projects = () => {
  const scrollToContact = () => {
    const element = document.querySelector('#contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="projects" className="py-24 bg-gradient-to-br from-gray-50 to-white">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Referenzen & Projekte
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Einblicke in unsere erfolgreichen Projekte und zufriedene Kunden in Reichling und Umgebung
            </p>
          </div>

          {/* Projects Grid */}
          {/* Projects Grid: 4 oben (je 3 col), 3 unten versetzt (je 4 col) auf lg */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-8 mb-16">
            {mockData.projects.map((project, index) => (
              <Card 
                key={index} 
                className={`group overflow-hidden border-0 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 ${index < 4 ? 'lg:col-span-3' : 'lg:col-span-4'}`}
                data-testid={`project-card-${index}`}
              >
                <div className="relative overflow-hidden">
                  <img 
                    src={project.image}
                    alt={project.title}
                    className="w-full h-56 object-cover group-hover:scale-110 transition-transform duration-300"
                    loading="lazy"
                  />
                  <div className="absolute top-4 right-4">
                    <span className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-3 py-1 rounded-full text-xs font-semibold">
                      {project.category}
                    </span>
                  </div>
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors">
                    {project.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {project.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Instagram Journey Einladung */}
          <div className="mb-16">
            <div className="bg-gradient-to-r from-[#0a1632] to-[#1a2642] rounded-3xl p-12 text-center text-white">
              <h3 className="text-3xl font-bold mb-6">
                Folgen Sie unserer Elektrotechnik-Journey!
              </h3>
              <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">
                Entdecken Sie auf Instagram unsere aktuellen Projekte, Installationen und Einblicke in die moderne Elektrotechnik.
              </p>
              
              <div className="flex flex-col lg:flex-row items-center justify-center gap-12">
                {/* QR Code Platzhalter */}
                <div className="bg-white rounded-2xl p-8 shadow-2xl">
                  <div className="w-48 h-48 bg-gray-100 rounded-lg flex flex-col items-center justify-center border-2 border-dashed border-gray-300">
                    <div className="text-6xl mb-4">📱</div>
                    <p className="text-gray-600 text-sm text-center font-medium">
                      Instagram<br/>QR Code<br/>
                      <span className="text-xs text-gray-500">Hier einfügen</span>
                    </p>
                  </div>
                </div>
                
                {/* Call to Action */}
                <div className="text-center lg:text-left max-w-md">
                  <h4 className="text-2xl font-bold mb-4 text-white">
                    @elektrotechnik_wegele
                  </h4>
                  <div className="space-y-4">
                    <div className="flex items-center justify-center lg:justify-start space-x-3">
                      <div className="w-3 h-3 bg-gradient-to-r from-cyan-400 to-blue-400 rounded-full"></div>
                      <span className="text-white/90">Live von der Baustelle</span>
                    </div>
                    <div className="flex items-center justify-center lg:justify-start space-x-3">
                      <div className="w-3 h-3 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full"></div>
                      <span className="text-white/90">Moderne Installationen</span>
                    </div>
                    <div className="flex items-center justify-center lg:justify-start space-x-3">
                      <div className="w-3 h-3 bg-gradient-to-r from-purple-400 to-cyan-400 rounded-full"></div>
                      <span className="text-white/90">Elektrotechnik-Tipps</span>
                    </div>
                  </div>
                  <Button 
                    className="mt-6 bg-gradient-to-r from-purple-500 via-pink-500 to-orange-400 hover:from-purple-600 hover:via-pink-600 hover:to-orange-500 text-white border-0 px-6 py-3 text-lg font-semibold shadow-xl transition-all duration-300 hover:scale-105"
                    onClick={() => window.open('https://instagram.com/elektrotechnik_wegele', '_blank')}
                  >
                    <Instagram className="mr-2 h-5 w-5" />
                    Jetzt folgen
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center">
            <div className="bg-white rounded-3xl p-12 shadow-xl border border-gray-100">
              <h3 className="text-3xl font-bold text-gray-900 mb-4">
                Werden Sie unser nächstes Erfolgsprojekt
              </h3>
              <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
                Lassen Sie uns gemeinsam Ihr Elektroprojekt planen und umsetzen. 
                Kostenlose Erstberatung und unverbindliches Angebot.
              </p>
              <Button 
                onClick={scrollToContact}
                size="lg"
                className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white border-0 px-8 py-4 text-lg font-semibold shadow-xl transition-all duration-300 hover:shadow-cyan-500/25 hover:scale-105"
              >
                Projekt besprechen
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Projects;